// TODO - Type 'any' needs to be fixed.
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import { Alert, Checkbox, FormControlLabel, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { GridOverlay } from "@mui/x-data-grid";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { JOB, SKILLS } from "../../../../../assets/constants";
import DataGrid from "../../../../../design/wilya-datagrid";
import useCopyShiftHolidays from "./controllers/use-copy-shift-holidays";
import useViewSummary from "./controllers/use-view-summary";
import HolidayAlert from "./holiday-alert";
import {
	CopyShiftsDataBySkillsProps,
	CopyShiftsDataProps,
	Shift,
} from "./types";
import ValidationAlert from "./validation-alert";

interface ViewSummaryProps {
	allWeekShifts: CopyShiftsDataProps[];
	allWeekShiftsBySkills: CopyShiftsDataBySkillsProps[];
	weekShiftsStatus: string;
	noJobSelected: boolean;
	noSkillSelected: boolean;
	selectedShiftIds: Set<number>;
	onShiftToggle: (shiftId: number) => void;
	onShiftSelectAll: (checked: boolean, shiftIds: number[]) => void;
	// Shifts left out because their copy would start before now
	pastShiftCount: number;
}

interface InvalidShiftProps extends Shift {
	isValid: boolean;
}

const NoShiftsAvailable = () => {
	const { t } = useTranslation();
	return (
		<GridOverlay>
			<Box display='flex' flexDirection='column' alignItems='center'>
				<CalendarTodayIcon
					sx={{ color: "rgba(0,0,0,0.38)" }}
					fontSize='large'
				/>
				<Typography
					sx={{
						color: "rgba(0,0,0,0.38)",
						fontFamily: "Roboto",
						fontSize: "14px",
						lineHeight: "20px",
						marginTop: "8px",
					}}
				>
					{t("scheduler_no-shifts-found")}
				</Typography>
			</Box>
		</GridOverlay>
	);
};

const ViewSummary = ({
	allWeekShifts,
	allWeekShiftsBySkills,
	weekShiftsStatus,
	noJobSelected,
	noSkillSelected,
	selectedShiftIds,
	onShiftToggle,
	onShiftSelectAll,
	pastShiftCount,
}: ViewSummaryProps) => {
	const { t } = useTranslation();
	const shiftsByFilter = useSelector(
		(state: any) => state.supervisor?.shiftsByFilter
	);

	const NoFilterSelected = () => {
		let filter = t("scheduler_job-role");
		let icon = (
			<WorkHistoryOutlinedIcon
				sx={{ color: "rgba(0,0,0,0.38)" }}
				fontSize='large'
			/>
		);
		switch (shiftsByFilter) {
			case SKILLS:
				filter = t("scheduler_skill");
				icon = (
					<BuildOutlinedIcon
						sx={{ color: "rgba(0,0,0,0.38)" }}
						fontSize='large'
					/>
				);
				break;
			default:
				break;
		}
		return (
			<GridOverlay>
				<Box display='flex' flexDirection='column' alignItems='center'>
					{icon}
					<Typography
						sx={{
							color: "rgba(0,0,0,0.38)",
							fontFamily: "Roboto",
							fontSize: "14px",
							lineHeight: "20px",
							marginTop: "8px",
						}}
					>
						{t("scheduler_select-filter-to-view-shifts", { filter })}
					</Typography>
				</Box>
			</GridOverlay>
		);
	};

	const noJobOrSkillSelected =
		(shiftsByFilter === JOB && noJobSelected) ||
		(shiftsByFilter === SKILLS && noSkillSelected);

	const holidayByShiftId = useCopyShiftHolidays(
		shiftsByFilter === SKILLS ? allWeekShiftsBySkills : allWeekShifts
	);
	const holidayTooltipByShiftId = useMemo(
		() =>
			new Map(
				[...holidayByShiftId.entries()].map(([shiftId, holiday]) => [
					shiftId,
					t("holidays_copy-row-tooltip", {
						name: holiday.holidays.map((h) => h.name).join(", "),
					}),
				])
			),
		[holidayByShiftId, t]
	);
	// Every listed shift whose copy lands on a holiday, selected or not, so
	// the banner (and its skip checkbox) stays put after they are skipped
	const holidayShiftIds = useMemo(
		() => [...holidayByShiftId.keys()],
		[holidayByShiftId]
	);
	const holidays = useMemo(
		() => [...holidayByShiftId.values()],
		[holidayByShiftId]
	);
	// Skipping = none of the holiday shifts is selected
	const isSkippingHolidays =
		holidayShiftIds.length > 0 &&
		holidayShiftIds.every((shiftId) => !selectedShiftIds.has(shiftId));
	const handleSkipHolidaysChange = useCallback(
		(isSkipping: boolean) => onShiftSelectAll(!isSkipping, holidayShiftIds),
		[onShiftSelectAll, holidayShiftIds]
	);

	const [{ minDurationHrs, maxDurationHrs, rows, selectedColumn }] =
		useViewSummary({
			allWeekShifts,
			allWeekShiftsBySkills,
			selectedShiftIds,
			onShiftToggle,
			holidayTooltipByShiftId,
		});

	const numberOfInvalidShifts = rows.reduce((acc: any, row: any) => {
		const count = row.shiftDetails.filter(
			(shift: InvalidShiftProps) => !shift.isValid
		).length;
		acc += count;
		return acc;
	}, 0);

	const allShiftIds: number[] = (
		shiftsByFilter === SKILLS ? allWeekShiftsBySkills : allWeekShifts
	).map((s: any) => s.id);
	const allSelected =
		allShiftIds.length > 0 &&
		allShiftIds.every((id) => selectedShiftIds.has(id));
	const someSelected =
		!allSelected && allShiftIds.some((id) => selectedShiftIds.has(id));

	return (
		<Box mt={2}>
			<Box display='flex' justifyContent='space-between' alignItems='center'>
				<Typography variant='subtitle2'>
					{t("scheduler_shifts-to-be-copied")}
				</Typography>
				{allShiftIds.length > 0 && (
					<FormControlLabel
						control={
							<Checkbox
								checked={allSelected}
								indeterminate={someSelected}
								onChange={(e) =>
									onShiftSelectAll(e.target.checked, allShiftIds)
								}
								size='small'
							/>
						}
						label={
							<Typography sx={{ fontSize: "13px", fontFamily: "Roboto" }}>
								{t("scheduler_select-all-shifts")}
							</Typography>
						}
						sx={{ mr: 0 }}
					/>
				)}
			</Box>
			{pastShiftCount > 0 && weekShiftsStatus !== "pending" ? (
				<Alert severity='info' sx={{ mt: 1 }}>
					{t("scheduler_copy-shifts-past-hidden", { count: pastShiftCount })}
				</Alert>
			) : null}
			<HolidayAlert
				holidays={holidays}
				isSkipping={isSkippingHolidays}
				onSkipChange={handleSkipHolidaysChange}
			/>
			{numberOfInvalidShifts > 0 ? (
				<ValidationAlert
					numberOfInvalidShifts={numberOfInvalidShifts}
					minDurationHrs={minDurationHrs ?? 0}
					maxDurationHrs={maxDurationHrs ?? 0}
				/>
			) : null}
			<Box
				width='100%'
				height={numberOfInvalidShifts > 0 || holidays.length > 0 ? 300 : 400}
				mt={3}
			>
				<DataGrid
					width='100%'
					height='100%'
					rows={rows ?? []}
					columns={selectedColumn}
					hideFooter={true}
					loading={weekShiftsStatus === "pending"}
					disableRowSelectionOnClick
					components={{
						NoRowsOverlay: noJobOrSkillSelected
							? NoFilterSelected
							: NoShiftsAvailable,
					}}
					getRowHeight={() => "auto"}
					sx={{
						border: "none !important",
						"& .MuiDataGrid-columnSeparator": {
							display: "none",
						},
						"& .MuiDataGrid-row.Mui-selected": {
							backgroundColor: "transparent",
						},
						"& .MuiDataGrid-row.Mui-selected:hover": {
							backgroundColor: "transparent",
						},
						"& .MuiDataGrid-row:hover": {
							backgroundColor: "transparent",
						},
						"& .MuiDataGrid-row:focus-within": {
							outline: "none",
						},
						"& .MuiDataGrid-cell:focus": {
							outline: "none",
						},
						"& .MuiDataGrid-cell:focus-within": {
							outline: "none",
						},
						overflow: "hidden",
					}}
				/>
			</Box>
		</Box>
	);
};

export default ViewSummary;
